import {
  isAddressNativeTokenPlaceholder,
  NATIVE_TOKEN_INTERNAL_PLACEHOLDER_ADDRESS,
  SOLANA_NATIVE_TOKEN_ADDRESS,
  SOLANA_NETWORK_ID,
} from "@repo/config";
import { Decimal } from "@repo/decimal";
import type {
  AssetIdFormat,
  AssetPairIdFormat,
  Caip19Id,
  EthOrSolanaAssetIdFormat,
} from "@repo/types";
import { Address, getAddress, type Hex } from "viem";
import {
  isValidSolanaAddress,
  serializeAddress,
  validateCryptoAddress,
} from "./address";
import { nanoid } from "./nanoid";

/** CAIP chain reference for Solana mainnet */
const SOLANA_MAINNET_CAIP_REFERENCE = "mainnet";
const SOLANA_MAINNET_GENESIS_HASH = "5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp";
export const SOL_SLIP44_COIN_TYPE = "501";

const normalizeSolanaChainIdForLegacyAssetId = (chainId: string): string => {
  if (
    chainId === SOLANA_MAINNET_CAIP_REFERENCE ||
    chainId === SOLANA_MAINNET_GENESIS_HASH
  ) {
    return SOLANA_NETWORK_ID.toString();
  }
  return chainId;
};

export const isSolanaChainId = (
  chainId: string | number | null | undefined,
): boolean => {
  if (chainId === null || chainId === undefined) {
    return false;
  }
  const normalizedChainId = normalizeSolanaChainIdForLegacyAssetId(
    chainId.toString(10),
  );
  return normalizedChainId === SOLANA_NETWORK_ID.toString();
};

export const isNativeSolSlip44 = (
  assetNamespace: string | null | undefined,
  assetReference: string | null | undefined,
): boolean =>
  assetNamespace === "slip44" && assetReference === SOL_SLIP44_COIN_TYPE;

export const toCanonicalSolAddress = (
  assetNamespace: string | null | undefined,
  assetReference: string | null | undefined,
  chainId: string | number | null | undefined,
): string | null => {
  if (!assetReference) {
    return null;
  }
  if (!isSolanaChainId(chainId)) {
    return assetReference;
  }
  if (
    isNativeSolSlip44(assetNamespace, assetReference) ||
    assetReference === SOL_SLIP44_COIN_TYPE
  ) {
    return SOLANA_NATIVE_TOKEN_ADDRESS;
  }
  return assetReference;
};

export const isCanonicalSolAddress = (
  address: string | null | undefined,
  chainId: string | number | null | undefined,
): boolean =>
  !!address &&
  isSolanaChainId(chainId) &&
  (address === SOLANA_NATIVE_TOKEN_ADDRESS ||
    address === SOL_SLIP44_COIN_TYPE);

// =============================================================================
// TODO: REMOVE BEFORE PRODUCTION - Legacy CAIP-19 format flag
// =============================================================================
// This flag exists to maintain backwards compatibility during MVP development.
// When true (default), ALL assets use EVM format (eip155/erc20) - even Solana.
// This is INCORRECT but keeps existing MVP code working.
//
// When false, Solana assets correctly use solana:mainnet/spl:<address> format.
//
// BEFORE PRODUCTION:
// 1. Change this default to `false`
// 2. Test everything still works
// 3. Remove this flag and the legacy code paths entirely
// =============================================================================
const USE_LEGACY_CAIP19_FORMAT_DEFAULT = true;
const CAIP19_SEPARATOR = "/";
const CAIP19_PARTS_SEPARATOR = ":";

export const getRandomId = (): string => {
  const guid = nanoid();
  return guid;
};

/**
 * Converts an asset_id (e.g., "0x...:8453" or "TokenMint...:1399811149") to a CAIP-19 string.
 * Supports both EVM chains (eip155/erc20) and Solana (solana/spl).
 * @param assetId The asset ID in the format 'address:chainId'.
 * @param useLegacyFormat - TODO: REMOVE BEFORE PRODUCTION. When true (default), uses
 *   EVM format for ALL assets including Solana. Set to false for correct Solana format.
 * @returns The CAIP-19 formatted string, or null if the format is invalid.
 */
export function assetIdToCaip19(
  assetId: AssetIdFormat,
  useLegacyFormat: boolean = USE_LEGACY_CAIP19_FORMAT_DEFAULT,
): Caip19Id | null {
  if (!assetId || !assetId.includes(":")) {
    console.warn(`[WARN] Invalid assetId format, skipping: ${assetId}`);
    return null;
  }
  const [address, chainId] = assetId.split(":");
  if (!address || !chainId) {
    console.warn(
      `[WARN] Invalid address or chainId in assetId, skipping: ${assetId}`,
    );
    return null;
  }

  // ==========================================================================
  // TODO: REMOVE BEFORE PRODUCTION - Legacy format treats everything as EVM
  // ==========================================================================
  if (useLegacyFormat) {
    return `eip155:${chainId}/erc20:${address}` as Caip19Id;
  }

  // Check if this is a Solana asset (by chainId or address format)
  const isSolana =
    chainId === SOLANA_NETWORK_ID.toString() || isValidSolanaAddress(address);

  if (isSolana) {
    // Solana uses solana:mainnet/spl:<address> format
    // Note: Solana addresses are case-sensitive (base58), do NOT lowercase
    return `solana:${SOLANA_MAINNET_CAIP_REFERENCE}/spl:${address}` as Caip19Id;
  }

  // EVM chains use eip155:<chainId>/erc20:<address> format
  return `eip155:${chainId}/erc20:${address.toLowerCase()}` as Caip19Id;
}

type Caip19Parts = {
  chainNamespace: string;
  chainReference: string;
  assetNamespace: string;
  assetReference: string;
};

const parseCaip19 = (value: string): Caip19Parts | null => {
  if (!value || typeof value !== "string") return null;
  const [chainPart, assetPart] = value.split(CAIP19_SEPARATOR);
  if (!chainPart || !assetPart) return null;
  const chainParts = chainPart.split(CAIP19_PARTS_SEPARATOR);
  const assetParts = assetPart.split(CAIP19_PARTS_SEPARATOR);
  if (chainParts.length !== 2 || assetParts.length < 2) return null;
  const [chainNamespace, chainReference] = chainParts;
  const [assetNamespace, ...assetRefParts] = assetParts;
  const assetReference = assetRefParts.join(CAIP19_PARTS_SEPARATOR);
  if (
    !chainNamespace ||
    !chainReference ||
    !assetNamespace ||
    !assetReference
  ) {
    return null;
  }
  return {
    chainNamespace,
    chainReference,
    assetNamespace,
    assetReference,
  };
};

/**
 * Converts an AssetIdFormat to CAIP-19 using canonical (non-legacy) rules.
 * This enforces correct Solana formatting instead of the legacy EVM-only mode.
 */
export const assetIdToCaip19Strict = (
  assetId: AssetIdFormat,
): Caip19Id | null => assetIdToCaip19(assetId, false);

/**
 * Converts a CAIP-19 string to AssetIdFormat using canonical (non-legacy) rules.
 * Supports legacy Solana CAIP-19 (eip155:<solanaChainId>/erc20:<mint>) by default.
 */
export const caip19ToAssetIdStrict = (
  caip19Id: Caip19Id | string,
  opts: { allowLegacySolana?: boolean } = {},
): AssetIdFormat | null => {
  const parsed = parseCaip19(caip19Id);
  if (!parsed) return null;

  const allowLegacySolana = opts.allowLegacySolana ?? true;

  if (parsed.chainNamespace === "solana") {
    return `${parsed.assetReference}:${SOLANA_NETWORK_ID}` as AssetIdFormat;
  }

  if (parsed.chainNamespace === "eip155") {
    if (parsed.assetNamespace === "slip44") {
      return `${NATIVE_TOKEN_INTERNAL_PLACEHOLDER_ADDRESS}:${parsed.chainReference}` as AssetIdFormat;
    }
    if (
      allowLegacySolana &&
      parsed.chainReference === SOLANA_NETWORK_ID.toString() &&
      isValidSolanaAddress(parsed.assetReference)
    ) {
      return `${parsed.assetReference}:${SOLANA_NETWORK_ID}` as AssetIdFormat;
    }
    return `${parsed.assetReference.toLowerCase()}:${parsed.chainReference}` as AssetIdFormat;
  }

  return null;
};

/**
 * Converts a CAIP-19 string back to the internal AssetIdFormat.
 * Supports both EVM (eip155/erc20) and Solana (solana/spl) formats.
 * @param caip19Id The CAIP-19 formatted string.
 * @param useLegacyFormat - TODO: REMOVE BEFORE PRODUCTION. When true (default), assumes
 *   all CAIP-19 strings are EVM format (even if they look like Solana). Set to false
 *   to correctly parse solana:mainnet/spl:<address> format.
 * @returns The internal asset ID ('address:chainId'), or null if the format is invalid.
 */
export function caip19ToAssetId(
  caip19Id: Caip19Id,
  useLegacyFormat: boolean = USE_LEGACY_CAIP19_FORMAT_DEFAULT,
): AssetIdFormat | null {
  if (!caip19Id || !caip19Id.includes("/") || !caip19Id.includes(":")) {
    console.warn(`[WARN] Invalid CAIP-19 format, skipping: ${caip19Id}`);
    return null;
  }
  const parts = caip19Id.split("/");
  if (parts.length !== 2) {
    return null;
  }
  const chainInfo = parts[0];
  const assetInfo = parts[1];

  const chainNamespace = chainInfo?.split(":")[0];
  const chainReference = chainInfo?.split(":")[1];
  const address = assetInfo?.split(":")[1];

  if (!address || !chainReference || !chainNamespace) {
    console.warn(
      `[WARN] Could not parse address or chainId from CAIP-19, skipping: ${caip19Id}`,
    );
    return null;
  }

  // ==========================================================================
  // TODO: REMOVE BEFORE PRODUCTION - Legacy format assumes everything is EVM
  // ==========================================================================
  if (useLegacyFormat) {
    // In legacy mode, chainReference is always the numeric chainId
    // Don't lowercase - preserve original address casing
    return `${address}:${chainReference}` as AssetIdFormat;
  }

  // Handle Solana CAIP-19 format (solana:mainnet/spl:<address>)
  if (chainNamespace === "solana") {
    // Solana addresses are case-sensitive - do NOT lowercase
    return `${address}:${SOLANA_NETWORK_ID}` as AssetIdFormat;
  }

  // Handle EVM CAIP-19 format (eip155:<chainId>/erc20:<address>)
  // chainReference is the numeric chainId for EVM
  return `${address.toLowerCase()}:${chainReference}` as AssetIdFormat;
}

export const first = <T>(arr: T[]): T | undefined => arr[0];

export const parseChainIdToNumber = (chainId: string | number): number => {
  return Number.parseInt(chainId.toString(10), 10);
};

/**
 * Helper function to get the assetId from a contract address and chainId
 * @param contractAddress
 * @param chainId
 * @returns
 */
export const getAssetId = (
  contractAddress: string,
  chainId: string | number,
): EthOrSolanaAssetIdFormat => {
  const normalizedChainId = normalizeSolanaChainIdForLegacyAssetId(
    chainId.toString(10),
  );
  const serializedAddress =
    normalizedChainId === SOLANA_NETWORK_ID.toString() &&
    contractAddress === SOL_SLIP44_COIN_TYPE
      ? SOLANA_NATIVE_TOKEN_ADDRESS
      : serializeAddress(contractAddress);
  return `${serializedAddress}:${normalizedChainId}`;
};

export const normalizeAssetId = (
  assetId: EthOrSolanaAssetIdFormat,
): EthOrSolanaAssetIdFormat => {
  const [address, chainId] = assetId.split(":");
  if (!address || !chainId) {
    throw new Error("Invalid assetId format");
  }
  const normalizedChainId = normalizeSolanaChainIdForLegacyAssetId(chainId);
  const serializedAddress =
    normalizedChainId === SOLANA_NETWORK_ID.toString() &&
    address === SOL_SLIP44_COIN_TYPE
      ? SOLANA_NATIVE_TOKEN_ADDRESS
      : serializeAddress(address);
  return `${serializedAddress}:${normalizedChainId}`;
};

export interface TokenMinimalAssetIdData {
  address: string;
  chainId: string;
}

// Alternative - object instead of array of args (re: getAssetId above)
export const getAssetIdFromTokenData = (
  token: TokenMinimalAssetIdData,
): EthOrSolanaAssetIdFormat => getAssetId(token.address, token.chainId);

export const isAssetIdNativeTokenPlaceholder = (
  assetId: EthOrSolanaAssetIdFormat,
) => {
  return isAddressNativeTokenPlaceholder(
    getAssetFromAssetId(assetId).contractAddress,
  );
};

/**
 * Helper function to get the contract address and chainId from an assetId
 * i.e. the reverse of getAssetId
 * @param assetId
 * @returns
 */
export const getAssetFromAssetId = (assetId: EthOrSolanaAssetIdFormat) => {
  const assetSplit = assetId.trim().split(":") as [string, string];
  if (assetSplit.length !== 2) {
    console.error("Invalid assetId format", assetId);
    throw new Error("Invalid assetId format");
  }
  const contractAddress = assetSplit[0]!;
  const chainId = normalizeSolanaChainIdForLegacyAssetId(assetSplit[1]!);
  if (
    chainId === SOLANA_NETWORK_ID.toString() &&
    contractAddress === SOL_SLIP44_COIN_TYPE
  ) {
    return {
      contractAddress: SOLANA_NATIVE_TOKEN_ADDRESS,
      chainId,
    };
  }
  const validatedAddress = validateCryptoAddress(contractAddress);
  if (!validatedAddress.isValid) {
    console.error("Invalid contract address", contractAddress);
    throw new Error("Invalid contract address");
  }
  if (validatedAddress.type === "solana") {
    return {
      contractAddress,
      chainId,
    };
  }
  const parsedContractAddress = getAddress(contractAddress);
  return {
    contractAddress: parsedContractAddress.toLowerCase() as Hex,
    chainId,
  };
};

export const getAssetPairId = (
  assetIdFrom: AssetIdFormat,
  assetIdTo: AssetIdFormat,
): AssetPairIdFormat => {
  return `${assetIdFrom}_${assetIdTo}`;
};

/**
 * Note: Pair ordering matters. This implies the swap direction -- this function will return the assetIdFrom and assetIdTo
 * FROMADDRESS:FROMCHAIN_TOADDRESS:TOCHAIN_ID
 * @param assetPairId
 * @returns
 */
export const getAssetPairFromAssetPairId = (assetPairId: AssetPairIdFormat) => {
  const assetPairSplit = assetPairId.split("_");
  if (assetPairSplit.length !== 2) {
    console.error("Invalid assetPairId format", assetPairId);
    throw new Error("Invalid assetPairId format");
  }

  const assetIdFrom = assetPairSplit[0]! as AssetIdFormat;
  const assetIdTo = assetPairSplit[1]! as AssetIdFormat;
  return { assetIdFrom, assetIdTo };
};

export type SwapCostsMap = Record<AssetPairIdFormat, string>;

export const generateSwapCosts = async (
  assetIds: string[],
  defaultSwapCostInUsd = 0,
): Promise<Record<AssetPairIdFormat, string>> => {
  const swapCostsMap: Record<AssetPairIdFormat, string> = {};

  for (let i = 0; i < assetIds.length; i++) {
    for (let j = i + 1; j < assetIds.length; j++) {
      const assetId1 = assetIds[i] as AssetIdFormat;
      const assetId2 = assetIds[j] as AssetIdFormat;
      const assetPairId1 = getAssetPairId(assetId1, assetId2);
      const assetPairId2 = getAssetPairId(assetId2, assetId1);
      swapCostsMap[assetPairId1] = defaultSwapCostInUsd.toString(10); // TODO(johnrjj)
      swapCostsMap[assetPairId2] = defaultSwapCostInUsd.toString(10); // TODO(johnrjj)
    }
  }

  return swapCostsMap;
};

const convertAllocationRecordToArray = (
  obj: Record<`0x${string}:${string}`, number>,
) =>
  Object.entries(obj).map(([k, v]) => ({
    assetId: k as AssetIdFormat,
    balanceInUsd: new Decimal(v),
  }));

type JSONValue = {
  [key: string]: JSONValue;
};

export type JSONCompatible<T> = unknown extends T
  ? never
  : {
      [P in keyof T]: T[P] extends JSONValue
        ? T[P]
        : T[P] extends NotAssignableToJson
          ? never
          : JSONCompatible<T[P]>;
    };

export type NotAssignableToJson = bigint | symbol | Function;

export function safeJsonStringify<T>(data: JSONCompatible<T>) {
  return JSON.stringify(data);
}

export function toJsonValue<T>(value: JSONCompatible<T>): JSONValue {
  return value;
}

export function safeJsonParse(text: string): unknown {
  return JSON.parse(text);
}

export function stringToNumber(str: string, maxNumExclusive: number): number {
  if (str.length === 0) {
    return 0;
  }

  let hash = 0;

  for (let i = 0; i < str.length; i++) {
    const chr = str.charCodeAt(i);
    hash = (hash << 5) - hash + chr;
    hash |= 0; // convert to 32bit integer
  }

  return Math.abs(hash) % maxNumExclusive;
}
